import { useState, useEffect } from "react";
import { updateLead, getLeadSelect, deleteLead } from "@/Api/leads";
import { getSelectClientOrigins } from "@/Api/clientOrigins";
import { getSelectClientCategories } from "@/Api/clientCategories";
import { getCet } from "@/Api/cep";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mask } from "@/utils/mask";
import { GENDERS } from "@/constants/gender";

// Status padrão das colunas do Kanban (com Venda concluída no final)
const DEFAULT_LEAD_STEPS = [
  { id: 1, name: "Novo Lead", color: "bg-blue-100 text-blue-800" },
  { id: 3, name: "Sem contato", color: "bg-red-100 text-red-800" },
  { id: 4, name: "Contato feito", color: "bg-yellow-100 text-yellow-800" },
  {
    id: 5,
    name: "Identificação do interesse",
    color: "bg-purple-100 text-purple-800",
  },
  { id: 6, name: "Apresentação", color: "bg-indigo-100 text-indigo-800" },
  { id: 2, name: "Venda concluída", color: "bg-green-100 text-green-800" },
];

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().min(1, "Telefone é obrigatório"),
  document: z.string().optional(),
  notes: z.string().optional(),
  observation: z.string().optional(),
  lead_step_id: z.string().min(1, "Status é obrigatório"),
  client_origin_id: z.string().min(1, "Origem é obrigatória"),
  seller_id: z.string().optional(),
  client_category_id: z.string().min(1, "Categoria é obrigatória"),
  gender_id: z.string().min(1, "Gênero é obrigatório"),
  birth_date: z.string().min(1, "Data de nascimento é obrigatória"),
  street: z.string().min(1, "Rua é obrigatória"),
  number: z.string().min(1, "Número é obrigatório"),
  neighborhood: z.string().optional(),
  city: z.string().min(1, "Cidade é obrigatória"),
  state: z.string().min(1, "Estado é obrigatório"),
  zip_code: z.string().min(1, "CEP é obrigatório"),
  reference: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EditLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: any;
  onLeadUpdated: () => void;
}

export function EditLeadDialog({
  open,
  onOpenChange,
  lead,
  onLeadUpdated,
}: EditLeadDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [steps, setSteps] = useState<{ id: string; name: string }[]>(
    DEFAULT_LEAD_STEPS.map((step) => ({
      id: step.id.toString(),
      name: step.name,
    }))
  );
  const [origins, setOrigins] = useState<{ id: string; name: string }[]>([]);
  const [sellers, setSellers] = useState<{ id: string; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    []
  );
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: lead.name,
      email: lead.email || "",
      phone: lead.phone,
      document: lead.document || "",
      notes: lead.notes || "",
      observation: lead.observation || "",
      lead_step_id: lead.lead_step_id.toString(),
      client_origin_id: lead.client_origin_id
        ? lead.client_origin_id.toString()
        : "",
      seller_id: lead.seller_id ? lead.seller_id.toString() : "",
      client_category_id: lead.client_category_id
        ? lead.client_category_id.toString()
        : "",
      gender_id: lead.gender_id ? lead.gender_id.toString() : "",
      birth_date: lead.birth_date || "",
      street: lead.addresses?.[0]?.street || "",
      number: lead.addresses?.[0]?.number || "",
      neighborhood: lead.addresses?.[0]?.neighborhood || "",
      city: lead.addresses?.[0]?.city || "",
      state: lead.addresses?.[0]?.state || "",
      zip_code: lead.addresses?.[0]?.zip_code || "",
      reference: lead.addresses?.[0]?.reference || "",
    },
  });

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      try {
        const { data } = await getCet(cep);
        if (!data.erro) {
          form.setValue("street", data.logradouro);
          form.setValue("neighborhood", data.bairro);
          form.setValue("city", data.localidade);
          form.setValue("state", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
        toast.error("Erro", {
          description: "Erro ao buscar o CEP. Tente novamente.",
        });
      }
    }
  };

  const loadData = async () => {
    try {
      const [leadSelectResponse, originsResponse, categoriesResponse] =
        await Promise.all([
          getLeadSelect(),
          getSelectClientOrigins(),
          getSelectClientCategories(),
        ]);

      if (leadSelectResponse.status) {
        if (
          leadSelectResponse.response.steps &&
          Array.isArray(leadSelectResponse.response.steps)
        ) {
          const serverSteps = leadSelectResponse.response.steps.map(
            (step: any) => ({
              id: step.id.toString(),
              name: step.name,
            })
          );

          const combinedSteps = [
            ...DEFAULT_LEAD_STEPS.map((step) => ({
              id: step.id.toString(),
              name: step.name,
            })),
          ];

          serverSteps.forEach((serverStep) => {
            if (!combinedSteps.some((s) => s.id === serverStep.id)) {
              combinedSteps.push(serverStep);
            }
          });

          setSteps(combinedSteps);
        }

        if (
          leadSelectResponse.response.sellers &&
          Array.isArray(leadSelectResponse.response.sellers)
        ) {
          setSellers(
            leadSelectResponse.response.sellers.map((seller: any) => ({
              id: seller.id.toString(),
              name: seller.user.name,
            }))
          );
        }
      }

      if (
        originsResponse.data?.response &&
        Array.isArray(originsResponse.data?.response)
      ) {
        setOrigins(
          originsResponse.data?.response?.map((origin: any) => ({
            id: origin?.id?.toString(),
            name: origin?.name,
          }))
        );
      }

      if (
        categoriesResponse.data?.response &&
        Array.isArray(categoriesResponse.data?.response)
      ) {
        setCategories(
          categoriesResponse.data?.response?.map((category: any) => ({
            id: category?.id?.toString(),
            name: category?.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
      form.reset({
        name: lead.name,
        email: lead.email || "",
        phone: lead.phone,
        document: lead.document || "",
        notes: lead.notes || "",
        observation: lead.observation || "",
        lead_step_id: lead.lead_step_id.toString(),
        client_origin_id: lead.client_origin_id
          ? lead.client_origin_id.toString()
          : "",
        seller_id: lead.seller_id ? lead.seller_id.toString() : "",
        client_category_id: lead.client_category_id
          ? lead.client_category_id.toString()
          : "",
        gender_id: lead.gender_id ? lead.gender_id.toString() : "",
        birth_date: lead.birth_date || "",
        street: lead.addresses?.[0]?.street || "",
        number: lead.addresses?.[0]?.number || "",
        neighborhood: lead.addresses?.[0]?.neighborhood || "",
        city: lead.addresses?.[0]?.city || "",
        state: lead.addresses?.[0]?.state || "",
        zip_code: lead.addresses?.[0]?.zip_code || "",
        reference: lead.addresses?.[0]?.reference || "",
      });
    }
  }, [open, lead, form]);

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    try {
      // Remover máscaras antes de enviar para a API
      const cleanedPhone = data.phone.replace(/\D/g, "");
      const cleanedDocument = data.document
        ? data.document.replace(/\D/g, "")
        : "";
      const cleanedZipCode = data.zip_code.replace(/\D/g, "");

      const result = await updateLead(lead.uuid, {
        name: data.name,
        email: data.email,
        phone: cleanedPhone,
        document: cleanedDocument,
        notes: data.notes,
        observation: data.observation,
        lead_step_id: parseInt(data.lead_step_id),
        client_origin_id: parseInt(data.client_origin_id),
        seller_id: data.seller_id ? parseInt(data.seller_id) : undefined,
        client_category_id: parseInt(data.client_category_id),
        gender_id: parseInt(data.gender_id),
        birth_date: data.birth_date,
        addresses: [
          {
            street: data.street,
            number: data.number,
            neighborhood: data.neighborhood || "",
            city: data.city,
            state: data.state,
            zip_code: cleanedZipCode,
            is_main: true,
            reference: data.reference || "",
          },
        ],
      });

      if (result.status) {
        toast.success("Sucesso", {
          description: "Lead atualizado com sucesso!",
        });

        onOpenChange(false);
        onLeadUpdated();
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Erro", {
        description: "Erro ao atualizar o lead. Tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteLead(lead.uuid);

      if (result.status) {
        toast.success("Sucesso", {
          description: "Lead excluído com sucesso!",
        });

        setShowDeleteAlert(false);
        onOpenChange(false);
        onLeadUpdated();
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Erro", {
        description: "Erro ao excluir o lead. Tente novamente.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Lead</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone*</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isLoading}
                          value={Mask.maskPhone(field.value || "")}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="document"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Documento (CPF/CNPJ)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isLoading}
                          value={Mask.maskCpfOrCnpj(field.value || "")}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Nascimento*</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gênero*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o gênero" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {GENDERS.map((gender) => (
                            <SelectItem
                              key={gender.id}
                              value={gender.id.toString()}
                            >
                              {gender.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="client_category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a categoria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="client_origin_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Origem*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a origem" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {origins.map((origin) => (
                            <SelectItem key={origin.id} value={origin.id}>
                              {origin.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <h3 className="font-medium text-sm">Endereço</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP*</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isLoading}
                          value={Mask.maskCEP(field.value || "")}
                          onChange={(e) => {
                            field.onChange(e);
                            handleCepChange(e);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rua*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="neighborhood"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referência</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado*</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isLoading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="lead_step_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {steps.map((step) => (
                            <SelectItem key={step.id} value={step.id}>
                              {step.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {sellers.length > 0 && (
                  <FormField
                    control={form.control}
                    name="seller_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vendedor</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          disabled={isLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o vendedor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {sellers.map((seller) => (
                              <SelectItem key={seller.id} value={seller.id}>
                                {seller.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <FormField
                control={form.control}
                name="observation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observações</FormLabel>
                    <FormControl>
                      <Textarea {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="flex justify-between gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  disabled={isLoading}
                  onClick={() => setShowDeleteAlert(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? <Spinner className="mr-2 h-4 w-4" /> : null}
                  Salvar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este lead? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
